import {
  Square,
  Move,
  GameVariant,
  VARIANT_SPECS,
  toGameVariant,
} from '@draughts/shared';
import { squareToPosition } from './square-utils.js';

/**
 * Move notation.
 *
 * The engine and the internal move representation always use square NUMBERS
 * (`Move.from`, `Move.to`, `Move.path`). This module only converts those
 * numbers into the human-readable string shown in the UI / PDN export.
 *
 * Two styles:
 * - `numeric` (default): square numbers, e.g. `28-24`, capture `28x17x6`.
 * - `algebraic`: chess-like coordinates a1..hN, e.g. `c3-d4`, capture `c3xe5xg7`.
 *   Used by Russian and Brazilian draughts.
 *
 * Separators are the same for both styles: `-` for a quiet move, `x` for a
 * capture (full path).
 */

/** Variants that display moves in algebraic (a1..hN) notation. */
const ALGEBRAIC_VARIANTS: ReadonlySet<GameVariant> = new Set([
  GameVariant.RUSSIAN,
  GameVariant.BRAZILIAN,
]);

const FILE_LETTERS = 'abcdefghijklmnopqrstuvwxyz';

/** A variant reference: the enum, or any accepted variant string. */
export type VariantRef = GameVariant | string;

function resolveVariant(variant: VariantRef): GameVariant | null {
  if (typeof variant !== 'string') return variant;
  try {
    return toGameVariant(variant);
  } catch {
    return null;
  }
}

/** True when the given variant uses algebraic (coordinate) notation. */
export function isAlgebraicVariant(variant: VariantRef): boolean {
  const resolved = resolveVariant(variant);
  return resolved !== null && ALGEBRAIC_VARIANTS.has(resolved);
}

/**
 * Convert a square NUMBER to algebraic coordinate (e.g. `c3`).
 *
 * File = column (col 1 -> `a`), rank = board height - row + 1 (row 1 is the top
 * of the board, i.e. the highest rank).
 *
 * `allSquaresPlayable` is the same flag `squareToPosition` takes, and for the
 * same reason: on Turkish/Dameo/Armenian/Croda the numbering is 1..N² row-major
 * over EVERY square, so without it a square number would be read as a dark-only
 * one and land on the wrong coordinate. Defaulted to `false`, i.e. every
 * existing 3-argument call keeps its meaning.
 */
export function squareToAlgebraic(
  square: Square,
  boardSize: number,
  darkSquareBottomRight: boolean,
  allSquaresPlayable: boolean = false,
): string {
  const { row, col } = squareToPosition(square, boardSize, darkSquareBottomRight, allSquaresPlayable);
  const file = FILE_LETTERS[col - 1] ?? '?';
  const rank = boardSize - row + 1;
  return `${file}${rank}`;
}

/**
 * Format a move as a notation string for the given variant.
 *
 * - Quiet move: `from` `-` `to`.
 * - Capture (numeric variants): full path `from` `x` (each landing square in
 *   `path`), e.g. `28x17x6`.
 * - Capture (algebraic variants): only the endpoints `from` `x` `to`, e.g.
 *   `c3xg7`.
 */
export function formatMoveNotation(move: Move, variant: VariantRef): string {
  const isCapture = move.captures.length > 0;
  const separator = isCapture ? 'x' : '-';

  const resolved = resolveVariant(variant);
  const useAlgebraic = resolved !== null && ALGEBRAIC_VARIANTS.has(resolved);

  const render = (square: Square): string => {
    if (!useAlgebraic || resolved === null) return String(square);
    const spec = VARIANT_SPECS[resolved];
    return squareToAlgebraic(square, spec.board.size, spec.ui.darkSquareBottomRight);
  };

  // Numeric variants keep the full capture path; algebraic variants show only
  // the start/end squares.
  const usePath = !useAlgebraic && isCapture && !!move.path && move.path.length > 0;
  const squares: Square[] = usePath ? [move.from, ...move.path!] : [move.from, move.to];

  return squares.map(render).join(separator);
}
